/**
 * STORY CARDS — the sticky picture's hand-over driver (Kyle, 2026-09-11:
 * "image on the left stays, swapping as scrolling, but text on the right
 * scrolls. New image swaps in as new paragraph comes to 50vh").
 *
 * Does exactly ONE thing: finds which copy has most recently crossed the
 * middle of the window and marks its picture, and every picture before it,
 * with data-eg-sc-shown. The pinning is CSS (position: sticky on the
 * pictures, section-eg-story-cards.css), the copies are in ordinary flow and
 * are never touched, and the dissolve is a CSS transition whose duration and
 * curve are the run's own tokens. No number of design value lives here.
 *
 * IT IS NOT eg-home-run.js, AND THE DIFFERENCE IS WHAT IT MEASURES. The run
 * pins its copy too, so nothing in flow is left to time and it has to divide
 * a stated runway into equal slots. Here the copies scroll, so each one's
 * arrival IS the thing to time: a copy's top edge against window.innerHeight
 * / 2, which is the 50vh Kyle named. Nothing is divided and no runway is
 * measured.
 *
 * A DEAD-BAND AROUND THE MIDDLE, in both directions. A reader parked on the
 * line, a trackpad settling, or a phone's momentum oscillating one pixel
 * would otherwise flip the mark back and forth and retrigger the whole fade
 * every time. So the switch forward happens --eg-hr-switch-band × the window
 * height ABOVE the middle and the switch back the same distance BELOW it;
 * between the two nothing changes whichever way the reader is drifting. The
 * token is the run's own, read off the track, unitless for the same reason
 * it is there: a fraction needs no unit conversion, a length would.
 *
 * BOTH LOOPS RUN TO EXHAUSTION. A fast flick past three copies advances the
 * index three times inside one call and the caller writes the result once,
 * so the browser resolves style for the final picture only and there is one
 * dissolve, not three queued.
 *
 * PICTURES 0…active ARE MARKED, NOT JUST THE ACTIVE ONE. They share one cell
 * and paint in DOM order, so everything below the active picture is hidden
 * behind it; marking them keeps the frame opaque all the way down and lets
 * the top one dissolve AWAY on the way back up, revealing the one beneath
 * rather than the page. The first picture is marked like any other and the
 * CSS simply gives it no opacity rule — it is the base layer, visible with no
 * script at all.
 *
 * FAIL-VISIBLE. data-eg-sc-ready is set only after a stack and at least one
 * picture-with-copy have been found, and every live CSS rule requires it.
 * No script, no pin, no fade — the section renders in its static grid.
 * data-eg-sc-armed arrives one animation frame after the first commit and
 * is what switches the transition on, so a reader who reloads part-way down
 * sees the right picture at once instead of watching it fade in.
 *
 * MOTION PREFERENCE AND WIDTH ARE NOT CHECKED HERE, ON PURPOSE. The marks are
 * written at every width and under every preference; the stylesheet's one
 * gated block is the single arbiter of whether a mark paints as anything.
 *
 * MOUNTS EVERY TRACK ON THE PAGE, guarded per track: /how-it-works carries
 * this section twice (six practice cards, three booklet cards), and the
 * section requests this file once per instance, so the script runs twice.
 */
(function () {
  function mount(track) {
    if (track.__egScMounted) return;
    track.__egScMounted = true;

    var stack = track.querySelector('[data-eg-sc-stack]');
    if (!stack) return;

    var stills = stack.querySelectorAll('.eg-storycard__media');
    if (!stills.length) return;

    /* Picture, then the copy it belongs to, is the markup's own contract —
       see sections/eg-story-cards.liquid. A picture with no copy after it is
       skipped rather than fatal. */
    /* `probe` IS THE FIRST THING IN THE COPY, NOT THE COPY'S BOX. The panel
       carries --eg-storycards-copy-pad of block padding, so its border box
       starts a full pad above the first word; "as the paragraph comes to
       50vh" means the words, so the eyebrow (or whatever leads) is what is
       measured. Falls back to the box for an empty panel. */
    var pairs = [];
    for (var i = 0; i < stills.length; i += 1) {
      var copy = stills[i].nextElementSibling;
      if (copy) {
        pairs.push({
          still: stills[i],
          copy: copy,
          probe: copy.firstElementChild || copy,
        });
      }
    }
    if (!pairs.length) return;

    var bandPx = 0;

    function measure() {
      var band = parseFloat(
        window.getComputedStyle(track).getPropertyValue('--eg-hr-switch-band'),
      );
      bandPx = band > 0 ? band * window.innerHeight : 0;
    }

    var active = 0;

    function resolve() {
      var mid = window.innerHeight / 2;
      while (
        active < pairs.length - 1 &&
        pairs[active + 1].probe.getBoundingClientRect().top < mid - bandPx
      ) {
        active += 1;
      }
      while (
        active > 0 &&
        pairs[active].probe.getBoundingClientRect().top > mid + bandPx
      ) {
        active -= 1;
      }
      return active;
    }

    var queued = false;

    function commit() {
      queued = false;
      var current = resolve();
      for (var i = 0; i < pairs.length; i += 1) {
        if (i <= current) pairs[i].still.setAttribute('data-eg-sc-shown', '');
        else pairs[i].still.removeAttribute('data-eg-sc-shown');
      }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(commit);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        measure();
        schedule();
      },
      { passive: true },
    );

    /* Ready first, then measure and commit in the same turn: the copies only
       take their rows once the live rules apply, and getBoundingClientRect()
       forces the layout, so the first commit reads the live geometry and no
       paint can land between the two statements. */
    track.setAttribute('data-eg-sc-ready', '');
    measure();
    commit();

    requestAnimationFrame(function () {
      track.setAttribute('data-eg-sc-armed', '');
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        measure();
        schedule();
      });
    }
  }

  var tracks = document.querySelectorAll('[data-eg-sc-track]');
  for (var i = 0; i < tracks.length; i += 1) mount(tracks[i]);
})();
